import CardCommon from '@/pages/opinion/components/CardCommon';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect, getLocale, useDispatch, useHistory } from 'umi';
import { KeywordItem, TableItem, WordCloudItem } from './components';
import {
  contrastString,
  initialUpperCase,
  sentimentType,
  supplementSingleData,
} from '@/pages/opinion/utils';
import { buriedBtnPoint } from '@/pages/opinion/operationLog/buriedPoint';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import { languageSelectOptions } from '@/pages/opinion/components/FilterBoxCommon/constant';
import { getFeedsKeywordText } from '@/pages/opinion/utils/languageValue';
import { LanguageType } from '@/pages/opinion/types/common';
import {
  cloudIcon,
  lineIcon,
  tableIcon,
} from '@/pages/opinion/constants/chart';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { othersList } from '@op/pages/stats/constants';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import SvgIcon from '@home/components/SvgIcon';
import useOverviewKeywordData from '../../hooks/useOverviewKeywrod';

const model = 'cloudKeyword';
function KeywordCard({
  filter,
  gameInfo,
  userInfo,
  curFilter,
  dealwithTimelineParams = (params: any) => params,
  dealwithCloudParams = (params: any) => params,
  contentCard = true,
  hasTitleArrow = true,
  title,
  wordCloudChartHeight = 278,
  lineHeight,
  tableHeight,
  className,
  chartClickAble = true,
  keywordClick,
  clickInfo = null,
  dropdownsFilterBeforeElement = null,
  needBrandHealth = false,
  hasKeywordDownPage = false,
  hasSubTitle = true,
  handleTitleOnClick,
  statsDetailType,
  downloadHead = 'Steam Reviews Detailed Keyword',
  cardBeforeElement = null,
  noTimeline = false,
  openUserCache = false,
}: any) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const { downloadLegacy, downloading } = useDownloadParams();
  const dispatch = useDispatch();
  const {
    overviewKeywordLoading,
    feedsKeywordLoading,
    feedsKeyword,
    overviewKeyword,
    requestTimeline,
    requestCloud,
  } = useOverviewKeywordData({ openUserCache });
  const [topicName, setTopicName] = useState(formatMessage({ id: 'All' }));
  const locale = useMemo(() => getLocale(), [formatMessage]);

  const [operation, setOperation] = useState('cloud');
  const [language, setLanguage] = useState<LanguageType>('origin');

  const axisTimeTypeMap: {
    [key: string]: 'day' | 'year' | 'days' | 'hourly';
  } = {
    daily: 'days',
    weekly: 'days',
    hourly: 'hourly',
    year: 'year',
  };

  const axisTimeType = axisTimeTypeMap[filter.date_type] || 'year';

  const timeObj: any = {
    days: 'MM-DD',
    year: 'YYYY-MM',
    hourly: 'MM-DD HH:mm',
  };
  const timeFormat = (star: string, end: string) => {
    const newStar = moment(star).format(timeObj[axisTimeType]);
    const newEnd = moment(end).format(timeObj[axisTimeType]);
    if (newStar === newEnd) return newStar;
    return `${newStar} ~ ${newEnd}`;
  };

  const [topicTime, setTopicTime] = useState(
    timeFormat(filter.start_time, filter.end_time),
  );

  const hasClickInfo = clickInfo !== null && clickInfo.seriesName !== null;

  const clickInfoParams = useMemo(() => {
    if (hasClickInfo) {
      const { seriesName = '' } = clickInfo;

      const curParams: any = {};
      curParams.topic = seriesName === 'unlabeled reviews' ? [] : [seriesName];
      if (clickInfo?.except_others) {
        curParams.except_others = clickInfo.except_others;
      }
      if (clickInfo?.except_topic) {
        curParams.except_topic = clickInfo.except_topic;
      }
      // moment 接受格式
      if (operation !== 'line' && clickInfo.name) {
        const timeTypeObj: any = {
          hourly: 'hour',
          daily: 'day',
          weekly: 'week',
          monthly: 'month',
        };
        curParams.start_time = moment(clickInfo.name)
          .startOf(timeTypeObj[filter?.date_type])
          .format('YYYY-MM-DD HH:mm:ss');
        curParams.end_time = moment(clickInfo.name)
          .endOf(timeTypeObj[filter?.date_type])
          .format('YYYY-MM-DD HH:mm:ss');
      }
      return curParams;
    }
    return {};
  }, [clickInfo, operation]);

  const chartClickInfoParams = useMemo(() => {
    const newParams = { ...clickInfoParams };
    if (newParams?.topic?.[0] === 'Others') {
      newParams.topic = [];
    }
    return newParams;
  }, [clickInfoParams]);

  const { Keyword_Color, Go_State_Show } = useGetSingePageSourceRule({
    keys: ['Keyword_Color', 'Go_State_Show'],
  });

  function getKeywordParams() {
    const notTotal = curFilter === 'negative' || curFilter === 'positive';
    const params: any = {
      keyword_sample_size: '500',
      sort_item: 'comment_time',
      sort_model: 'desc',
      sentiment_type: notTotal ? curFilter : '',
      topic_sentiment_type: curFilter,
      keyword_kind: curFilter === 'emerging' ? [curFilter] : [],
    };

    return {
      ...filter,
      ...gameInfo,
      ...params,
      ...clickInfoParams,
    };
  }

  useEffect(() => {
    if (!gameInfo || !filter) return;
    const allParams = getKeywordParams();
    if (hasClickInfo) {
      const { translationData = {}, seriesName = '', name = '' } = clickInfo;
      setTopicName(
        seriesName
          ? translationData?.[seriesName?.toLocaleLowerCase()]?.[locale] ||
              seriesName
          : formatMessage({ id: 'All' }),
      );
      setTopicTime(name || timeFormat(filter.start_time, filter.end_time));
    } else {
      setTopicName(formatMessage({ id: 'All' }));
      setTopicTime(timeFormat(filter.start_time, filter.end_time));
    }

    // 获取右边词云的相关数据
    requestCloud(
      dealwithCloudParams({
        ...allParams,
        model,
      }),
    );

    if (noTimeline) return;
    // 获取右边折线图的相关数据
    requestTimeline(
      dealwithTimelineParams({
        ...allParams,
        need_others: false,
        get_content: 'timeline_keywords',
      }),
    );
  }, [filter, curFilter, clickInfo, locale]);

  function downloadKeywordInfo() {
    const allParams = getKeywordParams();
    downloadLegacy({
      limit: 150000,
      params: dealwithCloudParams({
        ...allParams,
        model,
        user_id: userInfo.userid,
        hideSentimentRating: Keyword_Color,
      }),
      downloadFunc: 'getFeedsKeyword',
      simpleName: downloadHead,
      sheetName: downloadHead,
    });
  }

  let keywordLineData: any;
  if (overviewKeyword) {
    const { timeline_of_end_time, timeline_mentions, timeline_of_start_time } =
      overviewKeyword || {};

    const keywordData = supplementSingleData(
      timeline_of_start_time,
      timeline_of_end_time,
      timeline_mentions,
      filter.date_type || 'daily',
    );

    keywordLineData = {
      timeline_keywords: keywordData.filter(
        (item) => !othersList.includes(item.name),
      ),
      time_sum_line: overviewKeyword.time_sum_line,
    };
  }

  const [wordData, translateKeywordMap] = useMemo(() => {
    function getSentimentColorIndex(sentiment_rating: number) {
      return (
        (sentimentType(sentiment_rating, true, needBrandHealth) as number) - 1
      );
    }

    const translateKeywordMap = { origin: {}, en: {}, zh: {} };
    const feedsWordData = feedsKeyword?.map?.((v: any) => {
      const keywordKey = v.keyword.toLocaleLowerCase();
      translateKeywordMap.origin = {
        ...translateKeywordMap.origin,
        [keywordKey]: v.keyword,
      };
      translateKeywordMap.en = {
        ...translateKeywordMap.en,
        [keywordKey]: v.keyword_en,
      };
      translateKeywordMap.zh = {
        ...translateKeywordMap.zh,
        [keywordKey]: v.keyword_cn,
      };

      const item: any = {
        name: getFeedsKeywordText(v, language),
        value: v.count,
        mentions: v.count,
        sentiment: v.sentiment_rating,
        colorIndex: getSentimentColorIndex(v.sentiment_rating),
      };

      if (language === 'origin' && contrastString(v.keyword, v.keyword_en)) {
        v.child?.sort((a, b) => Number(b.cnt) - Number(a.cnt));
        const origin = v.child?.[0] || v;
        item.originName = initialUpperCase(origin.keyword);
        item.enName = v.keyword_en;
        item.showMore = !contrastString(item.originName, item.enName);
      }

      return item;
    });

    return [feedsWordData, translateKeywordMap];
  }, [feedsKeyword, language, needBrandHealth]);
  // console.log(translateKeywordMap, 'translateKeywordMap');
  const keywordIsLoading =
    operation === 'line' ? overviewKeywordLoading : feedsKeywordLoading;

  const keywordIsEmpty = useMemo(() => {
    if (operation === 'line') {
      return (
        !Array.isArray(keywordLineData?.timeline_keywords) ||
        keywordLineData?.timeline_keywords?.length === 0
      );
    }
    return !Array.isArray(wordData) || wordData?.length === 0;
  }, [operation, keywordLineData, wordData]);

  const history = useHistory();

  const chartFilter = useMemo(() => {
    if (hasKeywordDownPage) {
      return {
        ...filter,
        ...chartClickInfoParams,
      };
    }

    return { ...filter, ...chartClickInfoParams };
  }, [filter, chartClickInfoParams]);
  return (
    <CardCommon
      handleTitleOnClick={() => {
        if (Go_State_Show) return;
        if (handleTitleOnClick) handleTitleOnClick();
        else history.push('/opinion/Stats/Keyword');
      }}
      alwaysShowOperations
      className={className}
      hasDownload={!!downloadHead}
      downloadLoading={downloading}
      handleDownloadOnClick={downloadKeywordInfo}
      title={title || formatMessage({ id: 'Keyword' })}
      dropdowns={formatOptionsMessage(languageSelectOptions, undefined, {
        showIcon: true,
        placement: "top"
      })}
      dropdownFooter={
        <div style={{ textAlign: 'left', maxWidth: 204 }}>
          {formatMessage({
            id: 'From GPT-Translator. Powered by DataBrain NLP Team',
          })}
        </div>
      }
      handleDropdownOnChange={({ value }: { value: any }) => {
        buriedBtnPoint('TK_translate', value);
        setLanguage(value);
      }}
      dropdownsFilterBeforeElement={dropdownsFilterBeforeElement}
      contentCard={contentCard}
      hasTitleArrow={Go_State_Show ? false : hasTitleArrow}
      subTitle={
        hasSubTitle
          ? `${formatMessage({ id: 'Topic' })}: ${topicName} ${
              operation === 'line' ? '' : topicTime
            }`
          : ''
      }
      handleOperationsOnClick={({ symbol }: any) => {
        setOperation(symbol);
      }}
      defaultOperation="cloud"
      operations={
        noTimeline ? [tableIcon, cloudIcon] : [tableIcon, lineIcon, cloudIcon]
      }
      logBtnId="KeywordCard"
      otherSelectProps={{
        button: (option: { text: string }) => (
          <div className="dbo-select-button">
            <SvgIcon type="translate" className="dbo-select-button-icon" />
            <div className="tea-text-overflow">{option?.text}</div>
          </div>
        ),
      }}
    >
      <>
        {cardBeforeElement}
        <CardCommon
          loading={keywordIsLoading}
          loadingHeight={300}
          empty={keywordIsEmpty}
          emptyHeight={300}
          contentCard
        >
          <>
            {operation === 'line' && (
              <div className="echart-data">
                <KeywordItem
                  data={keywordLineData}
                  translateKeywordMap={translateKeywordMap[language]}
                  filter={chartFilter}
                  chartClickAble={chartClickAble}
                  height={lineHeight}
                  // keywordClick={keywordClick} 实时直播页面无法使用这个功能
                ></KeywordItem>
              </div>
            )}

            {operation === 'table' && (
              <TableItem
                data={wordData}
                keywordData={feedsKeyword}
                language={language}
                filter={chartFilter}
                // keywordClick={keywordClick}
                height={tableHeight}
                needBrandHealth={needBrandHealth}
                chartClickAble={chartClickAble}
              ></TableItem>
            )}

            {operation === 'cloud' && (
              <div className="echart-data">
                <WordCloudItem
                  height={wordCloudChartHeight}
                  wordData={wordData}
                  needBrandHealth={needBrandHealth}
                  keywordData={feedsKeyword}
                  language={language}
                  filter={chartFilter}
                  keywordClick={keywordClick}
                  chartClickAble={chartClickAble}
                  statsDetailType={statsDetailType}
                ></WordCloudItem>
              </div>
            )}
          </>
        </CardCommon>
      </>
    </CardCommon>
  );
}

export default connect((state: any, ownProps: any) => {
  const { loading, home, opinionCommon } = state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: ownProps.gameInfo || opinionCommon.gameInfo,
  };
})(KeywordCard);
